import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Container,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  Autocomplete,
  Button,
  Zoom,
  Fab,
  InputAdornment,
  Divider,
  useMediaQuery,
  useTheme,
  Paper,
} from "@mui/material";
import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "react-toastify";
import NavBar from "src/components/organisms/navbar";
import useAuthenticate from "src/hooks/useAuthenticate";
import api from "src/services/api";
import styled from "styled-components";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import PeopleIcon from "@mui/icons-material/People";
import { useLocation, useNavigate } from "react-router-dom";
import { PageLocationTitle, PageTitle } from "../subscriptions/styles";

const PageContainer = styled.div`
  padding: 1rem;
`;
const ListContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-direction: column;
`;

const UserManagement = () => {
  const location = useLocation();
  const [users, setUsers] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [visible, setVisible] = useState(false);
  const { getToken } = useAuthenticate();
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const userName = location?.state?.userName || search;
  const theme = useTheme();

  const roles = [
    { title: "Admin", option: "manager" },
    { title: "Dono de torneio", option: "tournament_owner" },
    { title: "Admin Pagamentos", option: "manager-payments" },
  ];

  const belt = [
    { title: "Branca", option: 1 },
    { title: "Cinza", option: 2 },
    { title: "Amarela", option: 3 },
    { title: "Laranja", option: 4 },
    { title: "Verde", option: 5 },
    { title: "Azul", option: 6 },
    { title: "Roxa", option: 7 },
    { title: "Marrom", option: 8 },
    { title: "Preta", option: 9 },
  ];

  const handleEditFighter = (userId: number) => {
    navigate(`/admin/users/edit/${userId}`);
  };

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchUsers = useCallback(
    async (pageNumber = 1, searchTerm = "", append = false) => {
      if (loading) return; // evita chamadas duplas
      setLoading(true);
      try {
        const response = await api.get(
          `/admin/users?${new URLSearchParams({
            page: pageNumber.toString(),
            limit: "50",
            search: searchTerm,
          })}`,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          }
        );

        const newUsers = response.data.data;

        setUsers((prev) => (append ? [...prev, ...newUsers] : newUsers));
        setPage(response.data.meta.page);
        setTotal(response.data.meta.total);
        setLastPage(response.data.meta.lastPage);
      } catch (error: any) {
        console.error(error);
        toast.error(
          error.response?.data?.message || "Erro ao carregar usuários"
        );
      } finally {
        setLoading(false);
      }
    },
    [getToken, loading]
  );

  useEffect(() => {
    fetchUsers(1, userName, false);

    if (location.state?.userName) {
      navigate(location.pathname, { replace: true, state: {} }); // limpa o state no histórico
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && page < lastPage && !loading) {
          fetchUsers(page + 1, search, true);
        }
      },
      {
        threshold: 0.1, // 👈 dispara quando ~10% visível
        rootMargin: "100px", // 👈 começa a carregar antes de chegar ao fim
      }
    );

    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [page, lastPage, search, fetchUsers, loading]);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center", // centraliza horizontalmente
            alignItems: "center", // centraliza verticalmente
            backdropFilter: "blur(6px)", // 👈 aplica o blur no fundo
            backgroundColor: "rgba(255, 255, 255, 0.4)", // leve transparência
            zIndex: 2000, // garante que fica acima de tudo
            gap: 2,
          }}
        >
          <Typography
            sx={{ fontFamily: "Inter, sans-serif", fontSize: "1.1rem" }}
          >
            Aguarde, estamos carregando os usuários...
          </Typography>

          <CircularProgress sx={{ color: "black" }} />
        </Box>
      )}

      {isMobile ? (
        <>
          <NavBar />
          <Box maxWidth={"sm"}>
            <Box sx={{ display: "flex", flexDirection: "column", ml: 1 }}>
              <PageLocationTitle>Gerenciar Usuários</PageLocationTitle>
              <PageTitle>{total} inscrições encontradas</PageTitle>
            </Box>

            {users.map((user) => (
              <>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontFamily: "inter, sans-serif" }}
                    >
                      {user.name.toLocaleUpperCase()}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails>
                    {user.UserRole.length > 0 && (
                      <>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          {user.UserRole.map((role: any) => (
                            <Chip
                              key={role.role.name}
                              label={roles
                                .filter(
                                  (item) => item.option === role.role.name
                                )[0]
                                ?.title?.toUpperCase()}
                              sx={{
                                mb: 1,
                                backgroundColor: "orange",
                                color: "black",
                                fontWeight: "bold",
                                fontFamily: "inter, sans-serif",
                              }}
                              onClick={() => undefined}
                            />
                          ))}
                        </Box>
                      </>
                    )}

                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{ fontFamily: "inter, sans-serif" }}
                    >
                      {"Email: "}
                      {user.email}
                    </Typography>
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{ fontFamily: "inter, sans-serif" }}
                    >
                      {"Academia: "}
                      {user.gym?.toLocaleUpperCase()}
                    </Typography>
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{ fontFamily: "inter, sans-serif" }}
                    >
                      {"Telefone: "}
                      {user.phone}
                    </Typography>
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{ fontFamily: "inter, sans-serif" }}
                    >
                      {"Genero: "}
                      {user.genre?.toLocaleUpperCase()}
                    </Typography>
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{ fontFamily: "inter, sans-serif" }}
                    >
                      {"Faixa: "}
                      {user.belt
                        ? belt.find((bel: any) => bel.option === user.belt)
                            ?.title
                        : "N/A"}
                    </Typography>
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{ fontFamily: "inter, sans-serif" }}
                    >
                      {"Criado em: "}
                      {new Date(user.created_at).toLocaleDateString()}
                    </Typography>

                    {user.Fighters?.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Divider sx={{ mb: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                          <PeopleIcon fontSize="small" />
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "bold", fontFamily: "inter, sans-serif" }}
                          >
                            Dependentes ({user.Fighters.length})
                          </Typography>
                        </Box>
                        {user.Fighters.map((fighter: any) => (
                          <Accordion
                            key={fighter.id}
                            sx={{
                              boxShadow: "none",
                              border: "1px solid #e0e0e0",
                              mb: 0.5,
                              "&::before": { display: "none" },
                            }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Chip
                                  label="DEPENDENTE"
                                  size="small"
                                  sx={{
                                    backgroundColor: "#1a2252",
                                    color: "white",
                                    fontWeight: "bold",
                                    fontFamily: "inter, sans-serif",
                                    fontSize: "0.65rem",
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: "inter, sans-serif", fontWeight: 500 }}
                                >
                                  {fighter.name?.toUpperCase()}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              {[
                                { label: "Email", value: fighter.email },
                                { label: "Academia", value: fighter.gym?.toUpperCase() },
                                { label: "Telefone", value: fighter.phone },
                                { label: "Gênero", value: fighter.genre?.toUpperCase() },
                                {
                                  label: "Faixa",
                                  value: fighter.belt
                                    ? belt.find((b: any) => b.option === fighter.belt)?.title
                                    : null,
                                },
                                { label: "Data de nascimento", value: fighter.birth_date },
                                { label: "Nome da mãe", value: fighter.mother_name },
                              ].map((item) => (
                                <Typography
                                  key={item.label}
                                  variant="body2"
                                  gutterBottom
                                  sx={{ fontFamily: "inter, sans-serif" }}
                                >
                                  {item.label}: {item.value || "N/A"}
                                </Typography>
                              ))}
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Box>
                    )}

                    <Button
                      sx={{ backgroundColor: "#373737", color: "white", mt: 1 }}
                      onClick={() => handleEditFighter(user.id)}
                    >
                      Editar
                    </Button>
                  </AccordionDetails>
                </Accordion>
              </>
            ))}
            <div ref={loaderRef} />
          </Box>
        </>
      ) : (
        <>
          <NavBar />
          <Container>
            <PageContainer>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <PageLocationTitle>Gerenciar Usuários</PageLocationTitle>
              </Box>
              <PageTitle>{total} Usuários encontrados</PageTitle>

              <TextField
                label="Pesquisar usuários"
                variant="outlined"
                size="small"
                value={searchInput}
                onChange={(e) => {
                  if (e.target.value === "") {
                    fetchUsers(1, "", false);
                    setSearch("");
                  }

                  setSearchInput(e.target.value);
                }}
                sx={{
                  width: "100%",
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon
                        onClick={() => {
                          fetchUsers(1, searchInput, false);
                          setSearchInput(searchInput);
                        }}
                        sx={{ cursor: "pointer" }}
                      >
                        <SearchIcon fontSize="small" />
                      </SearchIcon>
                    </InputAdornment>
                  ),
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    fetchUsers(1, searchInput, false);
                    setSearch(searchInput);
                  }
                }}
              />

              <ListContainer>
                {users.map((user: any, index: number) => (
                  <Accordion
                    key={user.id}
                    sx={{
                      width: "100%",
                      borderRadius: 2,
                      "&::before": { display: "none" },
                      bgcolor: index % 2 === 0 ? "white" : "grey.200",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <ExpandMoreIcon />
                        </Box>
                      }
                      sx={{ height: 20 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: 16,
                            fontWeight: 550,
                            fontFamily: "inter, sans-serif",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {user.name?.toUpperCase() || "N/A"}
                        </Typography>
                        <EditIcon
                          sx={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation(); // evita abrir/fechar o accordion ao clicar no edit
                            handleEditFighter(user.id);
                          }}
                        />

                        <Divider orientation="vertical" flexItem />

                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "inter, sans-serif" }}
                        >
                          {user.belt
                            ? belt
                                .filter((item) => item.option === user.belt)[0]
                                .title?.toUpperCase()
                            : "N/A"}
                        </Typography>

                        <Divider orientation="vertical" flexItem />

                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "inter, sans-serif" }}
                        >
                          {user.genre?.toUpperCase() || "N/A"}
                        </Typography>

                        <Divider orientation="vertical" flexItem />

                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "inter, sans-serif" }}
                        >
                          {user.gym?.toUpperCase() || "N/A"}
                        </Typography>

                        {user.UserRole.length > 0 && (
                          <>
                            <Divider orientation="vertical" flexItem />
                            {user.UserRole.map((role: any) => (
                              <Chip
                                key={role.role.name}
                                label={roles
                                  .filter(
                                    (item) => item.option === role.role.name
                                  )[0]
                                  ?.title?.toUpperCase()}
                                sx={{
                                  cursor: "pointer",
                                  backgroundColor: "orange",
                                  color: "black",
                                  fontWeight: "bold",
                                  fontFamily: "inter, sans-serif",
                                }}
                                onClick={() => undefined}
                              />
                            ))}
                          </>
                        )}
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        height: "20%",
                      }}
                    >
                      {[
                        { label: "Data de nascimento", value: user.birth_date },
                        { label: "Email", value: user.email },
                        { label: "Nome da mãe", value: user.mother_name },
                        { label: "Telefone", value: user.phone },
                        {
                          label: "Criado em",
                          value: new Date(user.created_at).toLocaleDateString(),
                        },
                        {
                          label: "Código de senha",
                          value: user.password_code,
                        },
                      ].map((item, index, arr) => (
                        <Box key={item.label}>
                          <Typography variant="body2">
                            <strong>{item.label}:</strong> {item.value || "N/A"}
                          </Typography>
                          {/* só renderiza o divider se não for o último */}
                          {index < arr.length - 1 && (
                            <Divider orientation="horizontal" flexItem />
                          )}
                        </Box>
                      ))}

                      {user.tournaments.length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            Dono dos Torneios:
                          </Typography>

                          {user.tournaments.map((tourn: any) => (
                            <Typography key={tourn.id}>{tourn.name}</Typography>
                          ))}
                        </Box>
                      )}

                      {user.Fighters?.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Divider sx={{ mb: 1 }} />
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                            <PeopleIcon fontSize="small" />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "bold" }}
                            >
                              Dependentes ({user.Fighters.length})
                            </Typography>
                          </Box>
                          {user.Fighters.map((fighter: any) => (
                            <Accordion
                              key={fighter.id}
                              sx={{
                                boxShadow: "none",
                                border: "1px solid #e0e0e0",
                                mb: 0.5,
                                "&::before": { display: "none" },
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{ height: 20 }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Chip
                                    label="DEPENDENTE"
                                    size="small"
                                    sx={{
                                      backgroundColor: "#1a2252",
                                      color: "white",
                                      fontWeight: "bold",
                                      fontFamily: "inter, sans-serif",
                                      fontSize: "0.7rem",
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: "inter, sans-serif",
                                      fontWeight: 550,
                                    }}
                                  >
                                    {fighter.name?.toUpperCase()}
                                  </Typography>
                                  <Divider orientation="vertical" flexItem />
                                  <Typography variant="body2" sx={{ fontFamily: "inter, sans-serif" }}>
                                    {fighter.belt
                                      ? belt.find((b: any) => b.option === fighter.belt)?.title?.toUpperCase()
                                      : "N/A"}
                                  </Typography>
                                  <Divider orientation="vertical" flexItem />
                                  <Typography variant="body2" sx={{ fontFamily: "inter, sans-serif" }}>
                                    {fighter.genre?.toUpperCase() || "N/A"}
                                  </Typography>
                                  <Divider orientation="vertical" flexItem />
                                  <Typography variant="body2" sx={{ fontFamily: "inter, sans-serif" }}>
                                    {fighter.gym?.toUpperCase() || "N/A"}
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails
                                sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                              >
                                {[
                                  { label: "Data de nascimento", value: fighter.birth_date },
                                  { label: "Email", value: fighter.email },
                                  { label: "Nome da mãe", value: fighter.mother_name },
                                  { label: "Telefone", value: fighter.phone },
                                  {
                                    label: "Criado em",
                                    value: fighter.created_at
                                      ? new Date(fighter.created_at).toLocaleDateString()
                                      : null,
                                  },
                                ].map((item, idx, arr) => (
                                  <Box key={item.label}>
                                    <Typography variant="body2">
                                      <strong>{item.label}:</strong> {item.value || "N/A"}
                                    </Typography>
                                    {idx < arr.length - 1 && (
                                      <Divider orientation="horizontal" flexItem />
                                    )}
                                  </Box>
                                ))}
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}

                {/* loader invisível que dispara o observer */}
                <div ref={loaderRef} />
              </ListContainer>
            </PageContainer>

            <Zoom in={visible}>
              <Fab
                color="primary"
                size="medium"
                onClick={scrollToTop}
                sx={{
                  position: "fixed",
                  bottom: 24,
                  right: 24,
                  backgroundColor: "#373737",
                  borderRadius: "50%",
                }}
              >
                <KeyboardArrowUpIcon />
              </Fab>
            </Zoom>
          </Container>
        </>
      )}
    </>
  );
};

export default UserManagement;
